import {Checkbox} from "components";
import {getName} from "helpers/string";
import CheckboxList from "mapx-components/Inputs/CheckboxList";
import React from "react";
import {TCity, TContinent, TCountry, TRegion, TState} from "../types";
import CityItem from "./CityItem";
import RegionItem from "./RegionItem";
import StateItem from "./StateItem";

type CountryItemProps = {
	country: TCountry;
	continentId: number;
	expanded: Record<number, boolean>;
	disabled: boolean;
	shouldRenderCountryRegions: boolean;
	isSelected: boolean;
	isPartiallySelected: boolean;
	hasChildren: boolean;
	onToggle: () => void;
	onToggleExpand: () => void;
	toggleExpandItem: (item: TContinent | TCountry | TRegion | TState) => void;
	toggleRegion: (region: TRegion, country: TCountry) => void;
	toggleState: (state: TState, region: TRegion, country: TCountry) => void;
	toggleCity: (city: TCity, state: TState, region: TRegion, country: TCountry) => void;
	isRegionSelected: (regionId: number, countryId: number) => boolean;
	isRegionPartiallySelected: (region: TRegion, country: TCountry) => boolean;
	isStateSelected: (stateId: number, regionId: number, countryId: number) => boolean;
	isStatePartiallySelected: (state: TState, region: TRegion, country: TCountry) => boolean;
	isCitySelected: (
		cityId: number,
		stateId: number,
		regionId: number,
		countryId: number,
	) => boolean;
};

const CountryItem: React.FC<CountryItemProps> = ({
	country,
	continentId,
	expanded,
	disabled,
	shouldRenderCountryRegions,
	isSelected,
	isPartiallySelected,
	hasChildren,
	onToggle,
	onToggleExpand,
	toggleExpandItem,
	toggleRegion,
	toggleState,
	toggleCity,
	isRegionSelected,
	isRegionPartiallySelected,
	isStateSelected,
	isStatePartiallySelected,
	isCitySelected,
}) => {
	const hasNoRegions = !country.regions || country.regions.length === 0;
	const hasNoStates = !country.states || country.states.length === 0;

	return (
		<div key={country.id}>
			<CheckboxList.AccordionChildrenContent
				expanded={expanded[continentId]}
				key={country.id}
				childrenExpanded={expanded[country.id]}
				handleExpandClick={onToggleExpand}
				hasChildren={hasChildren}
				treeIndex={1}
				item={country}
			>
				<Checkbox
					borderColor="#0C5850"
					isChecked={isSelected}
					partiallySelected={isPartiallySelected}
					key={country.id}
					label={getName(country.name)}
					onChange={onToggle}
					value={getName(country.name)}
					disabled={disabled}
				/>
			</CheckboxList.AccordionChildrenContent>

			{/* Regions */}
			{shouldRenderCountryRegions &&
				country.regions?.map((region) => (
					<RegionItem
						key={region.id}
						region={region}
						country={country}
						expanded={expanded}
						disabled={disabled}
						isSelected={isRegionSelected(region.id, country.id)}
						isPartiallySelected={isRegionPartiallySelected(region, country)}
						onToggle={() => toggleRegion(region, country)}
						onToggleExpand={() => toggleExpandItem(region)}
						onToggleState={toggleState}
						onToggleCity={toggleCity}
						isStateSelected={isStateSelected}
						isStatePartiallySelected={isStatePartiallySelected}
						isCitySelected={isCitySelected}
						toggleExpandItem={toggleExpandItem}
					/>
				))}
			{/* States directly on country (no regions) */}
			{shouldRenderCountryRegions &&
				hasNoRegions &&
				country.states?.map((state) => (
					<StateItem
						key={state.id}
						state={state}
						region={{id: 0, name: ""} as TRegion}
						country={country}
						expanded={expanded}
						disabled={disabled}
						isSelected={isStateSelected(state.id, 0, country.id)}
						isPartiallySelected={isStatePartiallySelected(
							state,
							{id: 0, name: ""} as TRegion,
							country,
						)}
						hasChildren={state.cities !== undefined && state.cities.length > 0}
						onToggle={() => toggleState(state, {id: 0, name: ""} as TRegion, country)}
						onToggleExpand={() => toggleExpandItem(state)}
						isCitySelected={isCitySelected}
						onToggleCity={toggleCity}
					/>
				))}

			{/* Cities directly on country (no regions/states) */}
			{shouldRenderCountryRegions &&
				hasNoRegions &&
				hasNoStates &&
				country.cities?.map((city) => (
					<CityItem
						key={city.id}
						city={city}
						state={{id: 0, name: "", cities: []} as TState}
						region={{id: 0, name: ""} as TRegion}
						country={country}
						expanded={expanded[country.id]}
						disabled={disabled}
						treeIndex={3}
						isSelected={isCitySelected(city.id, 0, 0, country.id)}
						onToggle={() =>
							toggleCity(
								city,
								{id: 0, name: "", cities: []} as TState,
								{id: 0, name: ""} as TRegion,
								country,
							)
						}
					/>
				))}
		</div>
	);
};

export default CountryItem;
